import { Helmet } from 'react-helmet-async';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicSiteUrl } from '../../config/siteConfig';

function declaresNoindex({ noindex, robots }) {
  if (noindex) return true;
  return String(robots || '').toLowerCase().includes('noindex');
}

/** Normalize robots for meta tags (Google prefers "index, follow"). */
function normalizeRobotsDirective(robots, noindex = false) {
  if (noindex && !robots) return 'noindex, nofollow';
  const raw = String(robots || '').trim();
  if (!raw) return 'index, follow';
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
}

/** Normalize keywords prop into a unique string[]. */
export function normalizeClientKeywords(keywords) {
  const out = [];
  const seen = new Set();
  const source = Array.isArray(keywords)
    ? keywords
    : typeof keywords === 'string'
      ? keywords.split(',')
      : keywords != null
        ? [keywords]
        : [];

  source.forEach((raw) => {
    const value = String(raw || '').trim();
    const key = value.toLowerCase();
    if (value && !seen.has(key)) {
      seen.add(key);
      out.push(value);
    }
  });
  return out;
}

/**
 * Remove prerender / static head tags Helmet cannot replace (no data-rh).
 * Without this, Inspect shows duplicate description, twitter:*, og:*, etc.
 */
function stripUnmanagedSeoHeadTags() {
  if (typeof document === 'undefined') return;
  const head = document.head;
  if (!head) return;

  const selectors = [
    'meta[name="description"]',
    'meta[name="keywords"]',
    'meta[name="title"]',
    'meta[name="author"]',
    'meta[name="robots"]',
    'meta[name^="twitter:"]',
    'meta[property^="og:"]',
    'meta[property^="article:"]',
    'link[rel="canonical"]',
  ];

  head.querySelectorAll(selectors.join(', ')).forEach((el) => {
    if (!el.hasAttribute('data-rh')) {
      el.parentNode?.removeChild(el);
    }
  });
}

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  articleAuthor,
  tags = [],
  keywords = [],
  noindex = false,
  robots,
  canonicalUrl,
  language = 'en',
  availableLanguages = {},
  alternateUrls = {},
  hreflangEnabled = true,
  xDefaultLanguage = 'en',
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  twitterCard = 'summary_large_image',
  twitterSite,
  googleSiteVerification,
  bingSiteVerification,
  strict = false,
}) => {
  const { t } = useTranslation();
  const siteName = t('site.name');
  const siteTagline = t('site.tagline');
  const siteUrl = getPublicSiteUrl();
  const hadPrerenderMeta = useRef(
    typeof document !== 'undefined' &&
      !!document.querySelector('meta[name="prerender-status"][content="ok"]')
  );
  const hadPrerenderHreflang = useRef(
    typeof document !== 'undefined' &&
      !!document.querySelector('link[rel="alternate"][hreflang]')
  );
  // On prerendered first paint, wait until real page SEO arrives before overwriting.
  const [headTagsActive, setHeadTagsActive] = useState(!hadPrerenderMeta.current);

  const toAbsoluteUrl = (pathOrUrl) => {
    if (!pathOrUrl) return '';
    const s = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(s)) return s;
    return `${siteUrl}${s.startsWith('/') ? s : `/${s}`}`;
  };

  const isNoindexPage = declaresNoindex({ noindex, robots });
  // Never fall back to the homepage canonical for article soft-404s / noindex pages —
  // that is exactly what GSC reported (noindex + canonical https://bloomwik.com/).
  const resolvedCanonical = canonicalUrl
    ? toAbsoluteUrl(canonicalUrl)
    : url
      ? toAbsoluteUrl(url)
      : strict || isNoindexPage
        ? ''
        : siteUrl;

  const robotsContent = normalizeRobotsDirective(robots, noindex);

  const pageTitle = (() => {
    if (!title) return strict ? siteName : `${siteName} - ${siteTagline}`;
    if (title.includes(siteName)) return title;
    return `${title} | ${siteName}`;
  })();

  const resolvedDescription = description || (strict ? '' : t('site.description'));
  const resolvedOgTitle = ogTitle || title || pageTitle;
  const resolvedOgDescription = ogDescription || description || resolvedDescription;
  const resolvedTwitterTitle = twitterTitle || resolvedOgTitle;
  const resolvedTwitterDescription = twitterDescription || resolvedOgDescription;
  const resolvedImage = image ? toAbsoluteUrl(image) : '';
  const keywordList = normalizeClientKeywords(keywords);
  const keywordsContent = keywordList.join(', ');

  const hreflangEntries = Object.entries(alternateUrls).filter(([key]) => key !== 'x-default');
  const clientHreflangReady = hreflangEntries.length > 0;
  const showHreflang =
    hreflangEnabled &&
    (clientHreflangReady || Object.keys(availableLanguages).length > 0);

  useEffect(() => {
    if (declaresNoindex({ noindex, robots })) {
      setHeadTagsActive(true);
      return;
    }
    if (title || description || url || canonicalUrl || keywordList.length) {
      setHeadTagsActive(true);
    }
  }, [noindex, robots, title, description, canonicalUrl, url, keywordList.length]);

  // Strip prerender duplicates before paint whenever we own the head.
  useLayoutEffect(() => {
    if (!headTagsActive) return;
    stripUnmanagedSeoHeadTags();
  }, [headTagsActive, resolvedCanonical, url, title, keywordsContent, pageTitle]);

  // Helmet applies metas on rAF — strip again after so prerender twins are gone.
  useEffect(() => {
    if (!headTagsActive) return undefined;
    const id = requestAnimationFrame(() => {
      stripUnmanagedSeoHeadTags();
    });
    return () => cancelAnimationFrame(id);
  }, [headTagsActive, resolvedCanonical, url, title, keywordsContent, pageTitle]);

  const renderClientHreflang =
    showHreflang &&
    clientHreflangReady &&
    !hadPrerenderHreflang.current;

  const hasPageSeo = Boolean(
    title || description || keywordList.length || (type === 'article' && (url || canonicalUrl))
  );
  const showCoreMeta = headTagsActive || hasPageSeo;

  // IMPORTANT: never wrap Helmet children in Fragments — react-helmet-async skips them.
  return (
    <Helmet>
      <html lang={language} />
      {showCoreMeta ? <title>{pageTitle}</title> : null}
      {/* meta name="title" kept for Inspect (Edge filters); mirrors <title> */}
      {showCoreMeta ? <meta name="title" content={pageTitle} /> : null}
      {showCoreMeta && resolvedDescription ? (
        <meta name="description" content={resolvedDescription} />
      ) : null}
      {showCoreMeta && author ? <meta name="author" content={author} /> : null}
      {keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
      {showCoreMeta ? <meta name="robots" content={robotsContent} /> : null}
      {showCoreMeta && googleSiteVerification ? (
        <meta name="google-site-verification" content={googleSiteVerification} />
      ) : null}
      {showCoreMeta && bingSiteVerification ? (
        <meta name="msvalidate.01" content={bingSiteVerification} />
      ) : null}
      {showCoreMeta && resolvedCanonical ? (
        <link rel="canonical" href={resolvedCanonical} />
      ) : null}

      {renderClientHreflang
        ? (hreflangEntries.length > 0
            ? hreflangEntries
            : Object.keys(availableLanguages).map((lang) => [lang, alternateUrls[lang]])
          ).map(([hreflang, path]) => {
            const langUrl = toAbsoluteUrl(path || alternateUrls[hreflang] || resolvedCanonical);
            if (!langUrl) return null;
            return (
              <link key={hreflang} rel="alternate" hreflang={hreflang} href={langUrl} />
            );
          })
        : null}
      {renderClientHreflang ? (
        <link
          rel="alternate"
          hreflang="x-default"
          href={toAbsoluteUrl(
            alternateUrls['x-default'] ||
              alternateUrls['en-us'] ||
              alternateUrls.en ||
              alternateUrls[xDefaultLanguage] ||
              resolvedCanonical
          )}
        />
      ) : null}

      {showCoreMeta ? <meta property="og:type" content={type} /> : null}
      {showCoreMeta && resolvedCanonical ? (
        <meta property="og:url" content={resolvedCanonical} />
      ) : null}
      {showCoreMeta && resolvedOgTitle ? (
        <meta property="og:title" content={resolvedOgTitle} />
      ) : null}
      {showCoreMeta && resolvedOgDescription ? (
        <meta property="og:description" content={resolvedOgDescription} />
      ) : null}
      {showCoreMeta && resolvedImage ? <meta property="og:image" content={resolvedImage} /> : null}
      {showCoreMeta ? <meta property="og:site_name" content={siteName} /> : null}
      {showCoreMeta ? (
        <meta
          property="og:locale"
          content={language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`}
        />
      ) : null}
      {showCoreMeta && showHreflang
        ? Object.keys(availableLanguages).map((lang) => {
            if (lang === language) return null;
            return (
              <meta
                key={`og:locale:alternate-${lang}`}
                property="og:locale:alternate"
                content={lang === 'en' ? 'en_US' : `${lang}_${lang.toUpperCase()}`}
              />
            );
          })
        : null}

      {showCoreMeta ? <meta name="twitter:card" content={twitterCard} /> : null}
      {showCoreMeta && twitterSite ? <meta name="twitter:site" content={twitterSite} /> : null}
      {showCoreMeta && resolvedCanonical ? (
        <meta name="twitter:url" content={resolvedCanonical} />
      ) : null}
      {showCoreMeta && resolvedTwitterTitle ? (
        <meta name="twitter:title" content={resolvedTwitterTitle} />
      ) : null}
      {showCoreMeta && resolvedTwitterDescription ? (
        <meta name="twitter:description" content={resolvedTwitterDescription} />
      ) : null}
      {showCoreMeta && resolvedImage ? (
        <meta name="twitter:image" content={resolvedImage} />
      ) : null}

      {showCoreMeta && type === 'article' && articleAuthor ? (
        <meta property="article:author" content={articleAuthor} />
      ) : null}
      {showCoreMeta && type === 'article' && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {showCoreMeta && type === 'article' && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}
      {showCoreMeta && type === 'article'
        ? tags.map((tag, index) => (
            <meta key={`article-tag-${index}`} property="article:tag" content={tag} />
          ))
        : null}
    </Helmet>
  );
};

export default SEO;
